import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { PagedResult } from '../../core/models/common.models';
import { RepositoryDetailResponse, RepositoryListItem } from '../../core/models/repository.models';
import { LocalDatePipe } from '../../shared/pipes/local-date.pipe';
import { StatusChipComponent } from '../../shared/components/status-chip/status-chip.component';

@Component({
  selector: 'app-repositories-page',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    RouterLink,
    LocalDatePipe,
    StatusChipComponent,
  ],
  templateUrl: './repositories-page.component.html',
  styleUrl: './repositories-page.component.scss',
})
export class RepositoriesPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly notice = signal<string | null>(null);
  readonly result = signal<PagedResult<RepositoryListItem> | null>(null);
  readonly search = signal('');
  readonly activeFilter = signal<'all' | 'active' | 'inactive'>('all');

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);
    this.notice.set(null);
    this.result.set(null);

    const activeSelection = this.activeFilter();
    const isActive =
      activeSelection === 'all' ? undefined : activeSelection === 'active';
    const query = {
      ...this.dateRange.toQueryParams(),
      search: this.search() || undefined,
      isActive,
      page: 1,
      pageSize: 20,
      sortBy: 'issuesFound',
      sortDirection: 'desc' as const,
    };

    this.analyticsService
      .getRepositories(query)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Repositories request failed', error);
          return this.loadFallbackRepositories(query, error);
        }),
        finalize(() => {
          if (loadId === this.currentLoadId) {
            this.loading.set(false);
          }
        }),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (loadId !== this.currentLoadId) {
            return;
          }

          try {
            if (!response?.paging || !Array.isArray(response?.items)) {
              throw new Error('Repository response is missing expected paging/items fields.');
            }

            this.result.set(this.normalizeRepositoryResult(response));
          } catch (mappingError) {
            console.error('Repositories response handling failed', mappingError, response);
            this.result.set(null);
            this.error.set('Repositories response could not be rendered. Check the browser console for the payload.');
          }
        },
        error: (error: unknown) => {
          if (loadId !== this.currentLoadId) {
            return;
          }

          console.error('Repositories fallback failed', error);
          this.error.set(this.formatError(error, 'Repositories could not be loaded.'));
        },
      });
  }

  private loadFallbackRepositories(
    query: {
      from?: string;
      to?: string;
      search?: string;
      isActive?: boolean;
      page: number;
      pageSize: number;
      sortBy: string;
      sortDirection: 'asc' | 'desc';
    },
    originalError: HttpErrorResponse,
  ) {
    return this.analyticsService.getOverview(this.dateRange.toQueryParams()).pipe(
      switchMap((overview) =>
        forkJoin(
          overview.topRepositories.map((item) =>
            this.analyticsService.getRepository(item.repositoryId, this.dateRange.toQueryParams()).pipe(
              map((detail) => this.mapRepositoryDetailToListItem(detail)),
              catchError((detailError) => {
                console.error(`Repository detail fallback failed for ${item.repositoryId}`, detailError);
                return of(null);
              }),
            ),
          ),
        ),
      ),
      map((items) => items.filter((item): item is RepositoryListItem => item !== null)),
      map((items) => this.filterFallbackItems(items, query)),
      map((items) => {
        this.notice.set(
          `Repository list endpoint returned ${originalError.status}. Showing fallback data built from overview and repository detail endpoints.`,
        );

        return this.normalizeRepositoryResult({
          items,
          paging: {
            page: 1,
            pageSize: items.length,
            totalCount: items.length,
            totalPages: 1,
          },
        } satisfies PagedResult<RepositoryListItem>);
      }),
    );
  }

  private mapRepositoryDetailToListItem(detail: RepositoryDetailResponse): RepositoryListItem {
    return {
      id: detail.id,
      name: detail.name,
      azureId: detail.azureId,
      isActive: detail.isActive,
      remoteUrl: detail.remoteUrl,
      defaultBranch: detail.defaultBranch ?? 'N/A',
      project: detail.project,
      organization: detail.organization,
      pullRequestsReviewed: detail.summary.pullRequestsReviewed,
      issuesFound: detail.summary.issuesFound,
      highIssues: detail.summary.highIssues,
      mediumIssues: detail.summary.mediumIssues,
      lowIssues: detail.summary.lowIssues,
      avgIssuesPerPullRequest: detail.summary.avgIssuesPerPullRequest,
      blockRate: detail.summary.blockRate,
      latestReviewAt: detail.summary.latestReviewAt,
    };
  }

  private filterFallbackItems(
    items: RepositoryListItem[],
    query: { search?: string; isActive?: boolean },
  ): RepositoryListItem[] {
    const normalizedSearch = query.search?.trim().toLowerCase();

    return items
      .filter((item) => (query.isActive === undefined ? true : item.isActive === query.isActive))
      .filter((item) => {
        if (!normalizedSearch) {
          return true;
        }

        const haystack = `${item.name} ${item.project.name} ${item.organization.name}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      })
      .sort((left, right) => right.issuesFound - left.issuesFound);
  }

  private normalizeRepositoryResult(
    response: PagedResult<RepositoryListItem>,
  ): PagedResult<RepositoryListItem> {
    const items = response.items.filter(
      (item) => item.pullRequestsReviewed > 0 || item.issuesFound > 0 || !!item.latestReviewAt,
    );

    return {
      ...response,
      items,
      paging: {
        ...response.paging,
        totalCount: items.length,
        totalPages: items.length > 0 ? 1 : 0,
      },
    };
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') {
      return 'Repositories request timed out after 8s. The backend is not responding for the selected range.';
    }

    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return `Repositories request could not reach the backend${error.url ? ` @ ${error.url}` : ''}. Check backend availability, CORS, and network access from the browser.`;
      }

      const details = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      const requestUrl = error.url ? ` @ ${error.url}` : '';
      return `${fallback} (${error.status || 'network'}${requestUrl}: ${details || error.message})`;
    }

    return fallback;
  }

}
